/**
 * AllocationTargetsModal.jsx — Modal for setting and editing asset-class allocation targets.
 * Used by the Overview tab to configure IPS-lite targets (e.g. 60% Equity, 20% Gold, 20% MF).
 */

import React, { useState, useEffect } from 'react';
import { getPortfolioSettings, updatePortfolioSettings } from '../../services/portfolioService';

const DEFAULT_CATEGORIES = [
    { category: 'Equity', targetPct: 0 },
    { category: 'Gold / Commodities', targetPct: 0 },
    { category: 'MF / Index Funds', targetPct: 0 },
];

const AllocationTargetsModal = ({ onClose, onSaved }) => {
    const [targets, setTargets] = useState(DEFAULT_CATEGORIES);
    const [driftThreshold, setDriftThreshold] = useState(5);
    const [concentrationThreshold, setConcentrationThreshold] = useState(25);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await getPortfolioSettings();
                const settings = res?.data ?? res;
                if (settings?.targetAllocation?.length) {
                    setTargets(settings.targetAllocation);
                }
                if (settings?.alertDefaults?.driftThresholdPct != null) {
                    setDriftThreshold(settings.alertDefaults.driftThresholdPct);
                }
                if (settings?.alertDefaults?.concentrationThresholdPct != null) {
                    setConcentrationThreshold(settings.alertDefaults.concentrationThresholdPct);
                }
            } catch { /* use defaults */ }
            setLoading(false);
        })();
    }, []);

    const totalPct = targets.reduce((s, t) => s + (parseFloat(t.targetPct) || 0), 0);

    const handleTargetChange = (idx, val) => {
        setTargets((prev) => prev.map((t, i) => i === idx ? { ...t, targetPct: parseFloat(val) || 0 } : t));
    };

    const handleSave = async () => {
        if (Math.abs(totalPct - 100) > 0.1 && totalPct > 0) {
            setError('Target allocations must sum to 100% (or leave all at 0 to disable).');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await updatePortfolioSettings({
                targetAllocation: targets,
                alertDefaults: {
                    driftThresholdPct: driftThreshold,
                    concentrationThresholdPct: concentrationThreshold,
                },
            });
            onSaved && onSaved();
            onClose();
        } catch (e) {
            setError(e?.response?.data?.error || 'Failed to save settings.');
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <div className="broker-connect-modal-overlay" onClick={onClose}>
                <div className="broker-connect-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="portfolio-loading">Loading settings…</div>
                </div>
            </div>
        );
    }

    return (
        <div className="broker-connect-modal-overlay" onClick={onClose}>
            <div className="broker-connect-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
                <h3 style={{ margin: '0 0 0.5rem' }}>Allocation Targets</h3>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', margin: '0 0 1rem' }}>
                    Set your desired asset-class allocation. The system will flag drift beyond your threshold.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                    {targets.map((t, i) => (
                        <div key={t.category} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{ flex: 1, fontWeight: 600, fontSize: '0.88rem' }}>{t.category}</span>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                value={t.targetPct}
                                onChange={(e) => handleTargetChange(i, e.target.value)}
                                style={{
                                    width: 70,
                                    padding: '0.35rem 0.5rem',
                                    borderRadius: 6,
                                    border: '1px solid var(--border)',
                                    background: 'var(--bg-card)',
                                    color: 'var(--text-1)',
                                    fontSize: '0.88rem',
                                    textAlign: 'right',
                                }}
                            />
                            <span style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>%</span>
                        </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '0.82rem', color: totalPct === 100 ? 'var(--green, #22c55e)' : totalPct > 0 ? 'var(--red, #ef4444)' : 'var(--text-2)' }}>
                        Total: {totalPct.toFixed(0)}%
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-2)' }}>Drift threshold</span>
                        <input
                            type="number"
                            min="1"
                            max="50"
                            step="1"
                            value={driftThreshold}
                            onChange={(e) => setDriftThreshold(parseFloat(e.target.value) || 5)}
                            style={{
                                width: 60,
                                padding: '0.3rem 0.4rem',
                                borderRadius: 6,
                                border: '1px solid var(--border)',
                                background: 'var(--bg-card)',
                                color: 'var(--text-1)',
                                fontSize: '0.85rem',
                                textAlign: 'right',
                            }}
                        />
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>%</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-2)' }}>Concentration threshold</span>
                        <input
                            type="number"
                            min="5"
                            max="100"
                            step="1"
                            value={concentrationThreshold}
                            onChange={(e) => setConcentrationThreshold(parseFloat(e.target.value) || 25)}
                            style={{
                                width: 60,
                                padding: '0.3rem 0.4rem',
                                borderRadius: 6,
                                border: '1px solid var(--border)',
                                background: 'var(--bg-card)',
                                color: 'var(--text-1)',
                                fontSize: '0.85rem',
                                textAlign: 'right',
                            }}
                        />
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>%</span>
                    </div>
                </div>

                {error && <p style={{ color: 'var(--red, #ef4444)', fontSize: '0.84rem', margin: '0.5rem 0' }}>{error}</p>}

                <div className="modal-actions">
                    <button className="broker-action-btn broker-action-btn--secondary" onClick={onClose}>Cancel</button>
                    <button className="broker-action-btn broker-action-btn--primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save Targets'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AllocationTargetsModal;
